import { AWARDS, RECORD_CATEGORIES, formatRecord } from '@shared/honors.js';
import { Link } from 'react-router-dom';
import { positions } from '@/utils/positions';
import { levels } from '@/utils/levels';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';

// A season's honors (seasons/{year}): league awards, each conference's
// champions and awards, and the records set that season.
const SeasonAwards = ({ season, teams, conferences, teamLink, pro }) => {

    const awards = season.awards;
    const confs = Object.values(conferences ?? {}).sort((a, b) => a.name.localeCompare(b.name));
    const team = (id) => teams[id];
    const teamLinkEl = (id) => team(id) ?
        <Link to={teamLink(id)} className='team-link'><TeamBadge team={team(id)} size='sm' /> <span>{team(id).name}</span></Link> :
        null;
    // New league records, plus near misses (the full lists are in the
    // record book). A league's first season sets every record, so only
    // the records themselves are listed then.
    const first = season.year === 1;
    const records = (season.newRecords ?? [])
        .filter(r => r.rank <= (first ? 1 : 3))
        .sort((a, b) => a.rank - b.rank);

    if (!awards) return <div className='g-card'><p className='meta'>Awards weren&apos;t given out this season.</p></div>;

    return (
        <div className='season-awards'>
            <div className='g-card'>
                <h3>League awards</h3>
                <div className='award-grid'>
                    {AWARDS.map(a => <AwardCard key={a.key} label={a.label} winner={awards.league?.[a.key]} team={team} teamLink={teamLink} />)}
                </div>
            </div>

            <div className='g-card'>
                <h3>Conferences</h3>
                <div className='table-scroll'>
                    <table className='g-table conf-honors'>
                        <thead>
                            <tr>
                                <th>Conference</th>
                                <th>Regular season</th>
                                {!pro && <th>Tournament</th>}
                                {AWARDS.map(a => <th key={a.key}>{SHORT[a.key]}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {confs.map(c => {
                                const champs = season.confChampions?.[c.id];
                                const confAwards = awards.conferences?.[c.id] ?? {};
                                return (
                                    <tr key={c.id}>
                                        <td><b>{c.name}</b></td>
                                        <td>{(champs?.regular ?? []).map(id => <div key={id}>{teamLinkEl(id)}</div>)}</td>
                                        {!pro && <td>{champs?.tournament ? teamLinkEl(champs.tournament) : '-'}</td>}
                                        {AWARDS.map(a => {
                                            const w = confAwards[a.key];
                                            return <td key={a.key}>{w ? <>{w.name} <span className='meta'>{w.abbr}</span></> : '-'}</td>;
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className='g-card'>
                <h3>Records set</h3>
                {first && <p className='meta'>The league&apos;s first season sets the first records.</p>}
                {records.length === 0 ? <p className='meta'>No records fell this season.</p> :
                    <ul className='records-set'>
                        {records.map(r => {
                            const cat = RECORD_CATEGORIES.find(c => c.key === r.category);
                            return (
                                <li key={`${r.category}-${r.playerId}`}>
                                    <span className={`record-rank ${r.rank === 1 ? 'first' : ''}`}>{r.rank === 1 ? 'Record' : `#${r.rank}`}</span>
                                    <span>{cat?.label}: <b>{formatRecord(cat, r.value)}</b>, {r.name} <span className='meta'>{r.abbr}{r.day ? ` · day ${r.day}` : ''}</span></span>
                                </li>
                            )
                        })}
                    </ul>
                }
            </div>
        </div>
    )
}

const SHORT = { poy: 'Player of the Year', opoy: 'Offensive', dpoy: 'Defensive', sixth: 'Sixth Player' };

const AwardCard = ({ label, winner, team, teamLink }) => {
    if (!winner) return null;
    const t = team(winner.teamId);
    const klass = winner.class ? levels[winner.class - 1]?.short : winner.age ? `Age ${winner.age}` : '';
    const l = winner.line;
    return (
        <div className='award-card'>
            <div className='award-label'>{label}</div>
            <div className='award-who'>
                {t && <TeamBadge team={t} size='md' />}
                <div>
                    <div className='award-name'>{winner.name}</div>
                    <div className='meta'>
                        {positions[winner.pos - 1]?.short} · {klass} · {t ? <Link to={teamLink(winner.teamId)}>{t.name}</Link> : winner.abbr}
                    </div>
                </div>
            </div>
            <div className='award-line'>
                {l.ppg} pts · {l.rpg} reb · {l.apg} ast · {l.spg} stl · {l.bpg} blk
            </div>
        </div>
    )
}

export default SeasonAwards
